import { h } from 'preact';
import UIElement from '../internal/UIElement/UIElement';
import PaypalComponent from './components/PaypalComponent';
import defaultProps from './defaultProps';
import AdyenCheckoutError from '../../core/Errors/AdyenCheckoutError';
import { ERRORS } from './constants';
import { TxVariants } from '../tx-variants';
import { formatPaypalOrderContactToAdyenFormat } from './utils/format-paypal-order-contact-to-adyen-format';

import type { ICore } from '../../core/types';
import type { PaymentAction, PaymentResponseData } from '../../types/global-types';
import type { Intent, PayPalConfiguration } from './types';
import type {
    PayPalOnApproveActions,
    PayPalOnApproveData,
    PayPalOnShippingAddressChangeActions,
    PayPalOnShippingAddressChangeData,
    PayPalOnShippingOptionsChangeActions,
    PayPalOnShippingOptionsChangeData,
    PayPalOrderResponseBody
} from './paypal-js-types';

import { AnalyticsInfoEvent, InfoEventType } from '../../core/Analytics/events/AnalyticsInfoEvent';
import { sanitizeResponse, verifyPaymentDidNotFail } from '../internal/UIElement/utils';
import CancelError from '../../core/Errors/CancelError';
import './Paypal.scss';

class PaypalElement extends UIElement<PayPalConfiguration> {
    public static readonly type = TxVariants.paypal;
    public static readonly subtype = 'sdk';

    public paymentData: string | null = null;

    private resolve: ((value: string) => void) | null = null;
    private reject: ((error?: Error) => void) | null = null;

    protected static readonly defaultProps = defaultProps;

    constructor(checkout: ICore, props?: PayPalConfiguration) {
        super(checkout, props);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleOnShippingAddressChange = this.handleOnShippingAddressChange.bind(this);
        this.handleOnShippingOptionsChange = this.handleOnShippingOptionsChange.bind(this);
    }

    formatProps(props: PayPalConfiguration): PayPalConfiguration {
        const merchantId = props.configuration?.merchantId;
        const intentFromConfig = props.configuration?.intent;
        const isZeroAuth = props.amount?.value === 0;
        const intent: Intent | undefined = isZeroAuth ? 'tokenize' : props.intent || intentFromConfig;
        const vault = intent === 'tokenize' || props.vault;

        const displayContinueToReviewPageButton = props.userAction === 'continue';

        return {
            ...props,
            commit: displayContinueToReviewPageButton ? false : props.commit,
            vault,
            configuration: {
                intent,
                merchantId
            }
        };
    }

    protected override beforeRender(configSetByMerchant?: PayPalConfiguration) {
        const event = new AnalyticsInfoEvent({
            type: InfoEventType.rendered,
            component: this.type,
            configData: { ...configSetByMerchant, showPayButton: this.props.showPayButton },
            ...(configSetByMerchant?.isExpress && { isExpress: configSetByMerchant.isExpress }),
            ...(configSetByMerchant?.expressPage && { expressPage: configSetByMerchant.expressPage })
        });

        this.analytics.sendAnalytics(event);
    }

    public submit = () => {
        this.handleError(new AdyenCheckoutError('IMPLEMENTATION_ERROR', ERRORS.SUBMIT_NOT_SUPPORTED));
    };

    /**
     * Updates the paymentData value. It must be used in the PayPal Express flow, when patching the amount
     * @param paymentData - Payment data value
     */
    public updatePaymentData(paymentData: string): void {
        if (!paymentData) console.warn('PayPal - Updating payment data with an invalid value');
        this.paymentData = paymentData;
    }

    /**
     * Formats the component data output
     */
    protected formatData() {
        const { isExpress, userAction } = this.props;

        return {
            paymentMethod: {
                type: PaypalElement.type,
                userAction,
                subtype: isExpress ? 'express' : PaypalElement.subtype
            }
        };
    }

    public handleAction = (action: PaymentAction) => {
        return this.updateWithAction(action);
    };

    public updateWithAction = (action: PaymentAction) => {
        if (action.paymentMethodType !== this.type) throw new Error('Invalid Action');

        if (action.paymentData) {
            this.paymentData = action.paymentData;
        }

        if (action.sdkData && action.sdkData.token) {
            this.onActionHandled({ componentType: this.type, actionDescription: 'sdk-loaded', originalAction: action });
            this.handleResolve(action.sdkData.token);
        } else {
            this.handleReject(ERRORS.NO_TOKEN_PROVIDED);
        }

        return null;
    };

    /**
     * Dropin Validation
     *
     * @remarks
     * Paypal does not require any specific Dropin validation
     */
    get isValid() {
        return true;
    }

    private readonly handleOnApprove = (data: PayPalOnApproveData, actions: PayPalOnApproveActions): Promise<void> => {
        const { onAuthorized } = this.props;

        const details = Object.entries(data).reduce<Record<string, string | undefined>>((result, [key, value]) => {
            result[key] = value ?? undefined;
            return result;
        }, {});

        const state = { data: { details, paymentData: this.paymentData ?? undefined } };

        if (!onAuthorized) {
            this.handleAdditionalDetails(state);
            return Promise.resolve();
        }

        if (!actions.order) {
            this.handleError(new AdyenCheckoutError('ERROR', 'PayPal order actions are not available'));
            return Promise.resolve();
        }

        return actions.order
            .get()
            .then((paypalOrder: PayPalOrderResponseBody) => {
                const billingAddress = formatPaypalOrderContactToAdyenFormat(paypalOrder?.payer);
                const deliveryAddress = formatPaypalOrderContactToAdyenFormat(paypalOrder?.purchase_units?.[0].shipping, true);

                this.setState({
                    authorizedEvent: paypalOrder,
                    ...(billingAddress && { billingAddress }),
                    ...(deliveryAddress && { deliveryAddress })
                });

                return new Promise<void>((resolve, reject) =>
                    onAuthorized(
                        {
                            authorizedEvent: paypalOrder,
                            ...(billingAddress && { billingAddress }),
                            ...(deliveryAddress && { deliveryAddress })
                        },
                        { resolve, reject }
                    )
                );
            })
            .then(() => this.handleAdditionalDetails(state))
            .catch(error => this.handleError(new AdyenCheckoutError('ERROR', 'Something went wrong while parsing PayPal Order', { cause: error })));
    };

    handleResolve(token: string) {
        if (!this.resolve) return this.handleError(new AdyenCheckoutError('ERROR', ERRORS.WRONG_INSTANCE));
        this.resolve(token);
    }

    handleReject(errorMessage: string) {
        if (!this.reject) return this.handleError(new AdyenCheckoutError('ERROR', ERRORS.WRONG_INSTANCE));
        this.reject(new Error(errorMessage));
    }

    private handleSubmit(): Promise<string> {
        return new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;

            this.makePaymentsCall()
                .then(sanitizeResponse)
                .then(verifyPaymentDidNotFail)
                .then(this.handleResponse)
                .catch((e: PaymentResponseData | Error) => {
                    if (e instanceof CancelError) {
                        this.setElementStatus('ready');
                        return;
                    }
                    this.handleFailedResult(e as PaymentResponseData);
                    const errorDetail = e instanceof Error ? e.message : JSON.stringify(e);
                    const errorMessage = e ? `: ${errorDetail}` : '';
                    this.handleReject(`${ERRORS.PAYMENT_FAILED}${errorMessage}`);
                });
        });
    }

    /**
     * If the merchant provides the 'onShippingAddressChange' callback, then this method is used as a wrapper to it, in order
     * to expose to the merchant the 'component' instance. The merchant needs the 'component' in order to manipulate the
     * paymentData
     *
     * @param data - PayPal data
     * @param actions - PayPal actions.
     */
    private handleOnShippingAddressChange(data: PayPalOnShippingAddressChangeData, actions: PayPalOnShippingAddressChangeActions): Promise<void> {
        const { onShippingAddressChange } = this.props;

        if (!onShippingAddressChange) return Promise.resolve();

        return onShippingAddressChange(data, actions, this);
    }

    /**
     * If the merchant provides the 'onShippingOptionsChange' callback, then this method is used as a wrapper to it, in order
     * to expose to the merchant the 'component' instance. The merchant needs the 'component' in order to manipulate the
     * paymentData
     *
     * @param data - PayPal data
     * @param actions - PayPal actions.
     */
    private handleOnShippingOptionsChange(data: PayPalOnShippingOptionsChangeData, actions: PayPalOnShippingOptionsChangeActions): Promise<void> {
        const { onShippingOptionsChange } = this.props;

        if (!onShippingOptionsChange) return Promise.resolve();

        return onShippingOptionsChange(data, actions, this);
    }

    protected override componentToRender(): h.JSX.Element | null {
        if (!this.props.showPayButton) return null;

        const { onShippingAddressChange, onShippingOptionsChange, ...rest } = this.props;

        return (
            <PaypalComponent
                {...rest}
                setComponentRef={this.setComponentRef}
                {...(onShippingAddressChange && { onShippingAddressChange: this.handleOnShippingAddressChange })}
                {...(onShippingOptionsChange && { onShippingOptionsChange: this.handleOnShippingOptionsChange })}
                onCancel={() => this.handleError(new AdyenCheckoutError('CANCEL'))}
                onChange={this.setState}
                onApprove={this.handleOnApprove}
                onError={error => {
                    this.handleError(new AdyenCheckoutError('ERROR', String(error), { cause: error }));
                }}
                onScriptLoadFailure={error => this.handleError(error)}
                onSubmit={this.handleSubmit}
            />
        );
    }
}

export default PaypalElement;
